import {computed, onBeforeUnmount, ref, watch, type Ref, unref} from 'vue'
import {useOverlayStack} from './useOverlayStack'

export interface UseOverlayOptions {
  element: Ref<HTMLElement | null>
  referenceElement?: Ref<HTMLElement | null>
  rootContainer?: Ref<HTMLElement | null>
  coverElement?: Ref<HTMLElement | null>
  onClose: () => void
  isOpen: Ref<boolean> | (() => boolean)
  dismissOnSiblingOverlay?: boolean
}

/** A layer that only covers the page, with no click-outside closing and no z-index from the stack, belongs to useCoveringLayer. */
export function useOverlay(options: UseOverlayOptions) {
  const {
    addOverlay,
    removeOverlay,
  } = useOverlayStack()

  const overlay = ref<ReturnType<typeof addOverlay> | null>(null)
  const overlayId = computed(() => overlay.value?.id ?? null)
  const zIndex = computed(() => overlay.value?.zIndex ?? null)

  const updateOverlay = () => {
    const isOpen = typeof options.isOpen === 'function' ? options.isOpen() : unref(options.isOpen)
    const element = unref(options.element)

    if (isOpen && element) {
      if (overlay.value) {
        removeOverlay(overlay.value.id)
      }
      overlay.value = addOverlay({
        element: element,
        referenceElement: options.referenceElement ? unref(options.referenceElement) : null,
        rootContainer: options.rootContainer ? unref(options.rootContainer) : null,
        coverElement: options.coverElement ? unref(options.coverElement) : null,
        onClose: options.onClose,
        dismissOnSiblingOverlay: options.dismissOnSiblingOverlay,
      })
    } else {
      if (overlay.value) {
        removeOverlay(overlay.value.id)
        overlay.value = null
      }
    }
  }

  watch(
    [
      () => (typeof options.isOpen === 'function' ? options.isOpen() : unref(options.isOpen)),
      () => unref(options.element),
      () => options.referenceElement ? unref(options.referenceElement) : null,
      () => options.rootContainer ? unref(options.rootContainer) : null,
      () => options.coverElement ? unref(options.coverElement) : null,
    ],
    () => {
      updateOverlay()
    },
    {
      immediate: true,
      flush: 'post',
    },
  )

  onBeforeUnmount(() => {
    if (overlay.value) {
      removeOverlay(overlay.value.id)
    }
  })

  return {
    overlayId,
    zIndex,
  }
}

